import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Calculator,
  Clock3,
  ReceiptText,
  ShieldCheck,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import {
  EmptyState,
  PageHeader,
  PageLayout,
  StatusBadge,
} from '@workspace/ui';
import { preloadRemoteDefinition } from '../../lib/remotes';
import {
  getLocalizedAppCategory,
  getLocalizedAppDescription,
  getLocalizedAppName,
} from '../../lib/app-i18n';
import { useAuthStore, type TenantApp } from '../../state/auth-store';

const appIcon: Record<string, LucideIcon> = {
  'time-tracking': Clock3,
  admin: ShieldCheck,
  'human-resources': UsersRound,
  invoice: ReceiptText,
  accounting: Calculator,
};

export function LauncherPage() {
  const { t } = useTranslation("layout");
  const { apps } = useAuthStore();

  if (!apps.length) {
    return (
      <PageLayout>
        <PageHeader
          eyebrow={t("launcher.eyebrow")}
          title={t("launcher.title")}
          subtitle={t("launcher.emptySubtitle")}
        />
        <EmptyState
          title={t("launcher.emptyTitle")}
          description={t("launcher.emptyDescription")}
        />
      </PageLayout>
    );
  }

  const groups = apps.reduce<Array<{ category: string; apps: TenantApp[] }>>(
    (result, app) => {
      const category = app.category || '';
      const existingGroup = result.find((group) => group.category === category);
      if (existingGroup) {
        existingGroup.apps.push(app);
      } else {
        result.push({ category, apps: [app] });
      }
      return result;
    },
    [],
  );
  const availableCount = apps.filter((app) => app.status === 'active').length;

  return (
    <PageLayout>
      <PageHeader
        eyebrow={t("launcher.eyebrow")}
        title={t("launcher.title")}
        subtitle={t("launcher.subtitle")}
        meta={
          <span className="rounded-pill bg-primary-subtle px-2.5 py-1 text-caption font-semibold text-content-accent">
            {t("launcher.availableCount", { count: availableCount })}
          </span>
        }
      />
      <div className="grid items-start gap-5 lg:grid-cols-2">
        {groups.map((group, groupIndex) => (
          <section
            key={group.category}
            aria-labelledby={`launcher-category-${groupIndex}`}
            className="overflow-hidden rounded-surface border border-border-subtle bg-surface-primary"
          >
            <div className="flex items-center justify-between gap-3 border-b border-border-subtle bg-surface-secondary/70 px-surface-comfortable-x py-surface-compact-y">
              <h2
                id={`launcher-category-${groupIndex}`}
                className="text-caption font-semibold uppercase tracking-[0.18em] text-content-secondary"
              >
                {getLocalizedAppCategory(t, group.category)}
              </h2>
              <span className="tabular-nums text-caption text-content-muted">
                {group.apps.length}
              </span>
            </div>
            <ul className="divide-y divide-border-subtle">
              {group.apps.map((app) => {
                const Icon = appIcon[app.key] ?? Calculator;
                const isAvailable = app.status === 'active';
                const appName = getLocalizedAppName(t, app);

                return (
                  <li
                    key={app.key}
                    className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 gap-y-3 px-surface-comfortable-x py-surface-comfortable-y sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center"
                  >
                    <Icon
                      className="mt-0.5 h-5 w-5 shrink-0 text-content-accent sm:mt-0"
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h3 className="font-display text-heading-sm font-semibold text-content-primary">
                          {appName}
                        </h3>
                        {app.status ? (
                          <StatusBadge tone={isAvailable ? 'success' : 'neutral'}>
                            {isAvailable
                              ? t("launcher.status.available")
                              : t("launcher.status.comingSoon")}
                          </StatusBadge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-body text-content-secondary">
                        {getLocalizedAppDescription(t, app)}
                      </p>
                    </div>
                    {isAvailable ? (
                      <Link
                        to={app.url}
                        aria-label={t("launcher.actions.openLabel", {
                          app: appName,
                        })}
                        onFocus={() => {
                          void preloadRemoteDefinition(app.key).catch(() => undefined);
                        }}
                        onPointerEnter={() => {
                          void preloadRemoteDefinition(app.key).catch(() => undefined);
                        }}
                        className="shell-launcher-row-action col-start-2 sm:col-start-3 sm:row-start-1"
                      >
                        <span>{t("launcher.actions.open")}</span>
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    ) : (
                      <span
                        aria-label={t("launcher.actions.unavailableLabel", {
                          app: appName,
                        })}
                        className="shell-launcher-row-action shell-launcher-row-action-disabled col-start-2 sm:col-start-3 sm:row-start-1"
                      >
                        {t("launcher.actions.unavailable")}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </PageLayout>
  );
}
